import React from "react";
import VehicleType from "./VehicleType";
import VehicleCategory from "./VehicleCategory";
import PowerSelector from "./PowerSelector";
import KilometerInput from "./KilometerInput";
import { calculateIndemnity } from "./calculator";

export default function App() {
    const [vehicleType, setVehicleType] = React.useState(null); // "thermal" | "electric"
    const [vehicleCategory, setVehicleCategory] = React.useState(null); // "auto" | "moto" | "cyclo"
    const [power, setPower] = React.useState(null);
    const [kilometers, setKilometers] = React.useState(0);
    const [result, setResult] = React.useState(null);
    const [copied, setCopied] = React.useState(false);

    function handleCopy() {
        if (result !== null) {
            const textToCopy = new Intl.NumberFormat('fr-FR', {
                style: 'currency',
                currency: 'EUR'
            }).format(result);

            navigator.clipboard.writeText(textToCopy).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            });
        }
    }

    // Reset dependent selections when parent changes
    React.useEffect(() => {
        setVehicleCategory(null);
        setPower(null);
    }, [vehicleType]);

    React.useEffect(() => {
        setPower(null);
    }, [vehicleCategory]);

    // Calculate result
    React.useEffect(() => {
        if (!vehicleType || !vehicleCategory || !power || !kilometers) {
            setResult(null);
            return;
        }

        const indemnity = calculateIndemnity(vehicleType, vehicleCategory, power, kilometers);
        setResult(indemnity);
    }, [vehicleType, vehicleCategory, power, kilometers]);

    const displayResult = result !== null
        ? new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(result)
        : "-";

    return (
        <>
            {/* Step 1: Vehicle Type */}
            <div className="mb-2 font-bold text-center w-full sm:text-lg cursor-default">
                Type de véhicule
            </div>
            <div className="flex w-full text-center gap-2 sm:gap-4">
                <VehicleType
                    type="thermal"
                    selected={vehicleType === "thermal"}
                    onClick={() => setVehicleType("thermal")}
                />
                <VehicleType
                    type="electric"
                    selected={vehicleType === "electric"}
                    onClick={() => setVehicleType("electric")}
                />
            </div>

            {/* Step 2: Vehicle Category */}
            <div className={`mt-4 transition-opacity duration-300 ${vehicleType ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
                <div className="mb-2 font-bold text-center w-full sm:text-lg cursor-default">
                    Catégorie de véhicule
                </div>
                <div className="flex w-full text-center gap-2 sm:gap-4">
                    <VehicleCategory
                        category="auto"
                        selected={vehicleCategory === "auto"}
                        onClick={() => setVehicleCategory("auto")}
                        disabled={!vehicleType}
                    />
                    <VehicleCategory
                        category="moto"
                        selected={vehicleCategory === "moto"}
                        onClick={() => setVehicleCategory("moto")}
                        disabled={!vehicleType}
                    />
                    <VehicleCategory
                        category="cyclo"
                        selected={vehicleCategory === "cyclo"}
                        onClick={() => setVehicleCategory("cyclo")}
                        disabled={!vehicleType}
                    />
                </div>
            </div>

            {/* Step 3: Power Selection */}
            <div className={`mt-4 transition-opacity duration-300 ${vehicleCategory ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
                <div className="mb-2 font-bold text-center w-full sm:text-lg cursor-default">
                    Puissance fiscale
                </div>
                <PowerSelector
                    vehicleCategory={vehicleCategory}
                    selectedPower={power}
                    onSelect={setPower}
                    disabled={!vehicleCategory}
                />
            </div>

            {/* Step 4: Kilometer Input */}
            <div className={`mt-4 p-4 bg-stone-100 rounded-lg transition-opacity duration-300 ${power ? 'opacity-100' : 'opacity-40 pointer-events-none'}`}>
                <KilometerInput
                    kilometers={kilometers}
                    onChange={setKilometers}
                    disabled={!power}
                />
            </div>

            {/* Result */}
            <div className="mb-2 mt-4 font-bold text-center w-full sm:text-lg cursor-default">
                Résultat
            </div>
            <div className="w-full bg-emerald-700 rounded-lg p-4 shadow-md flex flex-col justify-between">
                <div className="gap-2 flex items-center">
                    <div className="text-white">
                        <svg className="fill-current h-7 w-7" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" color="currentColor" fill="none">
                            <defs />
                            <path fill="currentColor" d="M11.33,2.026 C11.587,1.734 12.073,1.25 12.75,1.25 C13.426,1.25 13.912,1.734 14.17,2.026 C14.319,2.195 14.452,2.373 14.559,2.517 L14.599,2.572 C14.693,2.699 14.756,2.784 14.809,2.845 C14.956,3.016 15.046,3.098 15.127,3.146 C15.191,3.184 15.28,3.22 15.451,3.222 C15.627,3.225 15.818,3.169 16.164,3.067 L16.203,3.055 C16.514,2.964 16.989,2.825 17.492,2.9 C17.711,2.933 17.998,3.012 18.22,3.236 C18.483,3.5 18.526,3.83 18.487,4.079 C18.454,4.294 18.36,4.474 18.299,4.58 C18.24,4.681 18.172,4.779 18.127,4.845 L18.111,4.868 L17.178,6.225 L17.121,6.308 C16.997,6.491 16.875,6.669 16.74,6.834 C17.1,7.059 17.371,7.323 17.713,7.655 L17.74,7.681 C19.606,9.496 21.745,12.575 22.433,16.712 C22.724,18.462 22.052,19.997 20.897,21.069 C19.753,22.131 18.132,22.75 16.442,22.75 L9.059,22.75 C7.368,22.75 5.747,22.131 4.604,21.069 C3.449,19.997 2.777,18.462 3.068,16.712 C3.755,12.575 5.894,9.496 7.76,7.681 L7.787,7.655 C8.129,7.323 8.4,7.059 8.76,6.834 C8.625,6.669 8.503,6.491 8.378,6.308 L8.378,6.308 L8.321,6.225 L7.388,4.868 L7.373,4.845 C7.327,4.779 7.259,4.681 7.201,4.58 C7.139,4.474 7.046,4.294 7.012,4.079 C6.974,3.83 7.016,3.5 7.279,3.236 C7.502,3.012 7.788,2.933 8.007,2.9 C8.51,2.825 8.985,2.964 9.296,3.055 L9.335,3.067 C9.682,3.169 9.872,3.225 10.048,3.222 C10.22,3.22 10.309,3.184 10.373,3.146 C10.453,3.098 10.544,3.016 10.691,2.845 C10.744,2.784 10.806,2.699 10.9,2.572 L10.941,2.517 C11.047,2.373 11.18,2.195 11.33,2.026 Z M11.672,7.75 C10.657,7.75 10.322,7.76 9.912,7.927 C9.49,8.099 9.322,8.255 8.806,8.757 C7.12,10.397 5.172,13.199 4.547,16.958 C4.35,18.147 4.789,19.194 5.624,19.97 C6.471,20.756 7.72,21.25 9.059,21.25 L16.442,21.25 C17.78,21.25 19.029,20.756 19.876,19.97 C20.712,19.194 21.151,18.147 20.953,16.958 C20.328,13.199 18.381,10.397 16.694,8.757 C16.178,8.255 16.011,8.099 15.588,7.927 L15.588,7.927 C15.178,7.76 14.843,7.75 13.828,7.75 Z M13.342,10.992 L13.342,11.09 C14.217,11.314 14.89,11.946 15.101,12.722 C15.209,13.122 14.973,13.534 14.573,13.642 C14.173,13.75 13.761,13.514 13.653,13.114 C13.588,12.875 13.233,12.501 12.653,12.5 C12.634,12.502 12.615,12.502 12.597,12.503 L12.592,12.503 L12.573,12.502 C12.406,12.512 12.222,12.552 12.023,12.633 C11.583,12.812 11.469,13.127 11.477,13.254 C11.48,13.303 11.498,13.361 11.58,13.429 C11.673,13.505 11.878,13.612 12.275,13.648 C12.386,13.658 12.493,13.664 12.599,13.668 C12.679,13.67 12.759,13.672 12.84,13.673 L12.894,13.674 C13.059,13.676 13.246,13.679 13.425,13.694 C13.858,13.733 14.322,13.855 14.752,14.262 C15.227,14.71 15.443,15.454 15.326,16.134 C15.197,16.886 14.667,17.566 13.699,17.834 C13.58,17.867 13.461,17.894 13.342,17.916 L13.342,17.992 C13.342,18.406 13.006,18.742 12.592,18.742 C12.178,18.742 11.842,18.406 11.842,17.992 L11.842,17.896 C11.66,17.857 11.485,17.805 11.32,17.74 C10.664,17.485 10.007,16.968 9.869,16.181 C9.797,15.773 10.07,15.384 10.478,15.312 C10.886,15.241 11.275,15.514 11.346,15.921 C11.357,15.981 11.456,16.184 11.864,16.343 C12.073,16.424 12.33,16.475 12.605,16.479 C12.826,16.482 13.061,16.455 13.299,16.389 C13.523,16.327 13.646,16.236 13.715,16.159 C13.786,16.08 13.83,15.986 13.848,15.88 C13.891,15.631 13.787,15.413 13.723,15.353 C13.614,15.25 13.514,15.208 13.292,15.189 C13.173,15.178 13.043,15.176 12.864,15.174 L12.818,15.173 C12.751,15.172 12.678,15.171 12.602,15.168 C12.46,15.165 12.305,15.157 12.138,15.142 C11.536,15.086 11.018,14.907 10.63,14.59 C10.231,14.263 10.009,13.82 9.98,13.346 C9.923,12.425 10.575,11.604 11.456,11.244 C11.585,11.191 11.714,11.148 11.842,11.113 L11.842,10.992 C11.842,10.578 12.178,10.242 12.592,10.242 C13.006,10.242 13.342,10.578 13.342,10.992 Z M12.109,3.46 C12.022,3.578 11.92,3.717 11.826,3.826 C11.654,4.025 11.436,4.257 11.143,4.433 C10.832,4.619 10.48,4.716 10.069,4.722 C9.657,4.728 9.252,4.608 8.972,4.524 L9.557,5.376 C9.988,6.003 10.064,6.073 10.229,6.138 C10.334,6.179 10.485,6.213 10.773,6.231 C11.066,6.25 11.442,6.25 11.991,6.25 L13.508,6.25 C14.057,6.25 14.434,6.25 14.726,6.231 C15.014,6.213 15.165,6.179 15.27,6.138 C15.435,6.073 15.511,6.003 15.942,5.376 L16.528,4.524 C16.247,4.608 15.842,4.728 15.431,4.722 C15.019,4.716 14.667,4.619 14.357,4.433 C14.063,4.257 13.845,4.025 13.673,3.826 C13.579,3.717 13.477,3.578 13.39,3.46 L13.352,3.408 C13.247,3.266 13.147,3.134 13.045,3.018 C12.875,2.825 12.779,2.771 12.75,2.756 C12.72,2.771 12.624,2.825 12.455,3.018 C12.352,3.134 12.253,3.266 12.147,3.408 Z" />
                        </svg>
                    </div>
                    <div className="text-sm text-white sm:text-base w-full cursor-default" id="resultLabel">
                        Indemnité kilométrique
                    </div>
                </div>
                <div className="w-full text-center text-white h-full flex items-center my-2" aria-labelledby="resultLabel">
                    <div className="flex flex-col self-center w-full" aria-live="polite" aria-atomic="true">
                        <span className="font-bold text-3xl">{displayResult}</span>
                        <span className="text-sm font-semibold cursor-default">par an</span>
                    </div>
                </div>
                {result !== null && (
                    <button
                        onClick={handleCopy}
                        className="mt-2 flex items-center justify-center gap-2 w-full py-2 rounded-lg text-white text-sm font-semibold"
                        aria-label="Copier le résultat"
                    >
                        {copied ? (
                            <>
                                <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="20 6 9 17 4 12"></polyline>
                                </svg>
                                Copié !
                            </>
                        ) : (
                            <>
                                <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                </svg>
                                Copier
                            </>
                        )}
                    </button>
                )}
            </div>
        </>
    );
}